import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { regenerateScript, quickImproveScript, ScriptResponse } from '@/api';

interface Props {
  open: boolean;
  onClose: () => void;
  onScriptRegenerated: (script: ScriptResponse, improvement: any) => void;
  originalScript: any;
  projectId: string;
  currentRetentionScore?: number;
}

type ImproveType = 'hook' | 'pacing' | 'visuals' | 'tone' | 'overall';

const QUICK_ACTIONS = [
  {
    type: 'hook' as ImproveType,
    label: 'Better Hook',
    icon: '🎯',
    description: 'Create a more compelling, curiosity-driven opening'
  },
  {
    type: 'pacing' as ImproveType,
    label: 'Improve Pacing',
    icon: '⚡',
    description: 'Add pattern interrupts and engagement points'
  },
  {
    type: 'visuals' as ImproveType,
    label: 'More Visuals',
    icon: '🎨',
    description: 'Enhance visual cues and graphics directions'
  },
  {
    type: 'tone' as ImproveType,
    label: 'Make Casual',
    icon: '😊',
    description: 'More conversational and relatable language'
  },
  {
    type: 'overall' as ImproveType,
    label: 'Overall Polish',
    icon: '✨',
    description: 'Comprehensive improvement across all areas'
  }
];

export default function ScriptRegenerationModal({ 
  open, 
  onClose, 
  onScriptRegenerated, 
  originalScript, 
  projectId,
  currentRetentionScore 
}: Props) {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [customInstruction, setCustomInstruction] = useState('');
  const [error, setError] = useState('');
  const [mode, setMode] = useState<'quick' | 'custom'>('quick');

  const handleQuickImprove = async (improveType: ImproveType) => {
    setIsRegenerating(true);
    setError('');

    try {
      const response = await quickImproveScript({
        projectId,
        originalScript,
        improveType
      });
      
      onScriptRegenerated(response, response.improvement);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to improve script');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleCustomRegenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!customInstruction.trim()) {
      setError('Please provide specific feedback on what to improve');
      return;
    }

    setIsRegenerating(true);
    setError('');

    try {
      const response = await regenerateScript({
        projectId,
        originalScript,
        instruction: customInstruction
      });
      
      onScriptRegenerated(response, response.improvement);
      onClose();
      setCustomInstruction('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate script');
    } finally {
      setIsRegenerating(false);
    }
  };

  const resetAndClose = () => {
    setCustomInstruction('');
    setError('');
    setMode('quick');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            Regenerate Script
          </DialogTitle>
        </DialogHeader>

        {/* Current Score Display */}
        {currentRetentionScore && (
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Current Retention Score</p>
                <p className="text-2xl font-bold text-gray-900">{currentRetentionScore}/100</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Let's make it even better! 🚀</p>
              </div>
            </div>
          </div>
        )}

        {/* Mode Toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setMode('quick')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              mode === 'quick' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Quick Actions
          </button>
          <button
            onClick={() => setMode('custom')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              mode === 'custom' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Custom Feedback
          </button>
        </div>

        {mode === 'quick' ? (
          /* Quick Actions */
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Choose a specific area to improve with one click:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {QUICK_ACTIONS.map((action) => (
                <button
                  key={action.type}
                  onClick={() => handleQuickImprove(action.type)}
                  disabled={isRegenerating}
                  className="flex items-center gap-3 p-4 text-left border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                  <div className="text-2xl">{action.icon}</div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 group-hover:text-blue-900">
                      {action.label}
                    </h4>
                    <p className="text-sm text-gray-600 group-hover:text-blue-700">
                      {action.description}
                    </p>
                  </div>
                  <svg 
                    className="w-5 h-5 text-gray-400 group-hover:text-blue-500" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* Custom Feedback Form */
          <form onSubmit={handleCustomRegenerate} className="space-y-4">
            <div>
              <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-2">
                What would you like to improve?
              </label>
              <textarea
                id="feedback"
                value={customInstruction}
                onChange={(e) => setCustomInstruction(e.target.value)}
                placeholder="E.g., Make the hook more dramatic, add more personal stories, simplify the technical explanations..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={4}
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Be specific about what you want to change - the AI learns from your feedback!
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetAndClose}
                disabled={isRegenerating}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isRegenerating || !customInstruction.trim()}
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {isRegenerating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Regenerating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Regenerate Script
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Loading Overlay */}
        {isRegenerating && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-blue-600 font-medium">AI is improving your script...</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}